import lodash from "lodash";
import { useState } from "react";

import {
	Button,
	TableBody,
	TableCell,
	TableColumn,
	TableHeader,
	TableRow,
	addToast,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import {
	Chip,
	ErrorContent,
	OpenMode,
	UserDrawer,
} from "@onesolution/components";
import Table, {
	baseTableBodyProps,
	baseTableRowProps,
} from "@onesolution/components/Table/Table";
import { UserRolesColorMapping } from "@onesolution/constants";
import { useLanguage } from "@onesolution/hooks";
import { getUsersFromServer } from "@onesolution/serverFn/user";

export default function UsersPage() {
	const { t } = useLanguage();
	const {
		data,
		isError,
		refetch,
		isFetched,
		isFetching,
		fetchNextPage,
		hasNextPage,
	} = getUsersFromServer.useInfiniteQuery();
	const [drawerMode, setDrawerMode] = useState<OpenMode>(OpenMode.close);
	const [selectedUserId, setSelectedUserId] = useState<string>("");

	return (
		<div className="flex flex-col max-h-full">
			<div className="flex flex-row justify-between">
				<div className="flex flex-row gap-2 items-center mb-6">
					<h1 className="text-3xl font-bold">{t("name.users")}</h1>
					<Chip
						isLoading={isFetching}
						value={data?.items.length}
						variant="flat"
					/>
				</div>
				<Button
					variant="shadow"
					color="primary"
					startContent={
						<Icon icon="solar:add-circle-bold" className="size-5" />
					}
					onPress={() => setDrawerMode(OpenMode.creation)}
				>
					{t("basic.create")} {t("name.user")}
				</Button>
			</div>

			{isError && <ErrorContent isFetching={isFetching} refetch={refetch} />}

			<Table
				hasNextPage={hasNextPage}
				fetchNextPage={fetchNextPage}
				isFetching={isFetching}
			>
				<TableHeader>
					<TableColumn>{t("basic.lastname")}</TableColumn>
					<TableColumn>{t("basic.firstname")}</TableColumn>
					<TableColumn>{t("name.email")}</TableColumn>
					<TableColumn>{t("basic.role")}</TableColumn>
					<TableColumn>{t("name.services")}</TableColumn>
					<TableColumn>{t("basic.language")}</TableColumn>
					<TableColumn>{t("basic.active")}</TableColumn>
				</TableHeader>
				<TableBody
					isLoading={!isFetched}
					items={data?.items || []}
					{...baseTableBodyProps(t)}
				>
					{(user) => (
						<TableRow
							key={user.id}
							onClick={() => {
								setDrawerMode(OpenMode.details);
								setSelectedUserId(user.id);
							}}
							{...baseTableRowProps()}
						>
							<TableCell className="flex justify-between">
								{lodash.capitalize(user.lastname)}
							</TableCell>
							<TableCell>{lodash.capitalize(user.firstname)}</TableCell>
							<TableCell>
								<Chip
									className="group"
									radius="sm"
									variant="flat"
									endContent={
										<Icon
											icon="solar:clipboard-list-linear"
											className="w-0 transition-all duration-100 ease-in-out group-hover:w-4"
										/>
									}
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										navigator.clipboard.writeText(user.email);
										addToast({
											title: t("message.copied-to-clipboard"),
											color: "success",
										});
									}}
								>
									{user.email}
								</Chip>
							</TableCell>
							<TableCell>
								<Chip
									radius="sm"
									className="min-w-16"
									color={UserRolesColorMapping[user.role.label]}
								>
									{user.role.label}
								</Chip>
							</TableCell>
							<TableCell className="flex flex-row gap-2">
								{user.services.map((service) => (
									<Chip
										radius="sm"
										key={service}
										color={"secondary"}
										variant={
											user.favoriteServices.includes(service)
												? "solid"
												: "bordered"
										}
									>
										{service}
									</Chip>
								))}
							</TableCell>
							<TableCell>
								<Chip radius="sm" variant="flat">
									{user.language}
								</Chip>
							</TableCell>
							<TableCell>
								<Chip
									radius="sm"
									variant="dot"
									classNames={{
										dot: user.active ? "bg-success" : "bg-red-600",
									}}
								>
									{user.active ? t("basic.yes") : t("basic.no")}
								</Chip>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
			<UserDrawer
				mode={drawerMode}
				selectedUserId={selectedUserId}
				onClose={() => {
					setDrawerMode(OpenMode.close);
					setSelectedUserId("");
				}}
			/>
		</div>
	);
}
